import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import StructuredAdviceDisplay from '../components/StructuredAdviceDisplay';
import ProductCarousel from '../components/affiliate/ProductCarousel';
import api from '../api/api';
import '../styles/pages/ConsultationDetailPage.css';

/**
 * つむぎからのアドバイスページ（V2 - つむぎデザイン適用）
 * V1からの移行: ThemeContext削除、AppLayout/PageTitle使用
 */
const ConsultationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    document.title = 'つむぎからのアドバイス | はしもと栄養パートナー';
  }, []);

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    loadConsultation();
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    // スクロール位置の監視
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadConsultation = async () => {
    try {
      const response = await api.get(`/api/consultations/${id}`);
      if (response.data.success) {
        setConsultation(response.data.consultation);
      } else {
        setError('相談内容が見つかりませんでした');
      }
    } catch (err) {
      console.error('Failed to load consultation:', err);
      setError('相談内容の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleContinueConsultation = () => {
    navigate('/form-v2', {
      state: {
        continueFrom: consultation._id,
        previousConsultation: consultation
      }
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <PageTitle title="つむぎからのアドバイス" subtitle="読み込み中..." icon="📋" />
        <div className="tsumugi-container py-6">
          <div className="tsumugi-card text-center">
            <p className="text-hint">相談内容を読み込んでいます...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <PageTitle title="つむぎからのアドバイス" subtitle="エラー" icon="⚠️" />
        <div className="tsumugi-container py-6">
          <div className="tsumugi-card text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={() => navigate('/mypage')}
              className="tsumugi-btn tsumugi-btn-primary"
            >
              マイページへ戻る
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTitle
        title="つむぎからのアドバイス"
        subtitle={formatDate(consultation.createdAt)}
        icon="📋"
        mascot={<img src="/tsumugi.png" alt="つむぎ" className="page-title__mascot-img" />}
      />

      <div className="tsumugi-container py-6">
        {/* Consultation Header Card */}
        <div className="tsumugi-card consultation-detail__section">
          <div className="consultation-detail__header">
            <div className="consultation-detail__header-left">
              {consultation.isFollowUp && (
                <span className="tsumugi-tag tsumugi-tag-lilac">
                  継続相談
                </span>
              )}
            </div>
            {!consultation.isFollowUp && (
              <button
                onClick={handleContinueConsultation}
                className="tsumugi-btn tsumugi-btn-secondary text-sm"
              >
                この相談を続ける
              </button>
            )}
          </div>

          {/* Previous Consultation Link */}
          {consultation.previousConsultationId && (
            <div className="consultation-detail__prev-link-wrapper">
              <Link
                to={`/consultation/${typeof consultation.previousConsultationId === 'object' ? consultation.previousConsultationId._id || consultation.previousConsultationId.toString() : consultation.previousConsultationId}`}
                className="consultation-detail__prev-link"
              >
                <svg className="consultation-detail__prev-link-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                前回の相談を見る
              </Link>
            </div>
          )}
        </div>

        {/* User Input Section */}
        <div className="tsumugi-card consultation-detail__section">
          <h3 className="tsumugi-card-title">相談内容</h3>

          {consultation.userInput && (
            <div className="consultation-detail__user-input">
              {/* Basic Info */}
              <div className="consultation-detail__basic-info">
                {consultation.userInput.age && (
                  <div className="consultation-detail__info-item">
                    <span className="consultation-detail__info-label">年齢</span>
                    <span className="consultation-detail__info-value">{consultation.userInput.age}歳</span>
                  </div>
                )}
                {consultation.userInput.gender && (
                  <div className="consultation-detail__info-item">
                    <span className="consultation-detail__info-label">性別</span>
                    <span className="consultation-detail__info-value">
                      {consultation.userInput.gender === 'male' ? '男性' :
                       consultation.userInput.gender === 'female' ? '女性' :
                       consultation.userInput.gender}
                    </span>
                  </div>
                )}
                {consultation.userInput.height && (
                  <div className="consultation-detail__info-item">
                    <span className="consultation-detail__info-label">身長</span>
                    <span className="consultation-detail__info-value">{consultation.userInput.height}cm</span>
                  </div>
                )}
                {consultation.userInput.weight && (
                  <div className="consultation-detail__info-item">
                    <span className="consultation-detail__info-label">体重</span>
                    <span className="consultation-detail__info-value">{consultation.userInput.weight}kg</span>
                  </div>
                )}
              </div>

              {/* Condition */}
              {consultation.userInput.condition && (
                <div>
                  <span className="consultation-detail__section-title">橋本病の診断状況</span>
                  <p className="consultation-detail__info-value">
                    {consultation.userInput.condition}
                  </p>
                </div>
              )}

              {/* Symptoms */}
              {consultation.userInput.symptoms && consultation.userInput.symptoms.length > 0 && (
                <div>
                  <span className="consultation-detail__section-title">現在の症状</span>
                  <div className="consultation-detail__symptoms">
                    {consultation.userInput.symptoms.map((symptom, index) => {
                      // 症状を日本語に変換
                      const getJapaneseSymptom = (symptom) => {
                        const symptomMap = {
                          'fatigue': '疲労感',
                          'irritability': 'イライラ',
                          'swelling': 'むくみ',
                          'cold': '冷え性',
                          'sleep_disorder': '睡眠障害',
                          'concentration': '集中力低下',
                          'weight_gain': '体重増加',
                          'weight_loss': '体重減少',
                          'hair_loss': '抜け毛',
                          'dry_skin': '肌の乾燥',
                          'skin_dryness': '肌の乾燥',
                          'constipation': '便秘',
                          'depression': '気分の落ち込み',
                          'memory_loss': '記憶力低下',
                          'joint_pain': '関節痛',
                          'muscle_weakness': '筋力低下',
                          'muscle_pain': '筋肉痛・関節痛',
                          'menstrual': '月経異常'
                        };

                        if (symptom.includes('severity:')) {
                          const severity = symptom.split(':')[1].trim();
                          const severityMap = {
                            'mild': '軽度',
                            'moderate': '中程度',
                            'severe': '重度'
                          };
                          return `症状レベル: ${severityMap[severity] || severity}`;
                        }

                        if (symptom.includes('duration:')) {
                          const duration = symptom.split(':')[1].trim();
                          const durationMap = {
                            'short': '短期間',
                            'medium': '中期間',
                            'long': '長期間'
                          };
                          return `持続期間: ${durationMap[duration] || duration}`;
                        }

                        return symptomMap[symptom] || symptom;
                      };

                      return (
                        <span
                          key={index}
                          className="tsumugi-tag tsumugi-tag-peach"
                        >
                          {getJapaneseSymptom(symptom)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Lifestyle */}
              {consultation.userInput.lifestyle && (
                <div>
                  <span className="consultation-detail__section-title">生活習慣</span>
                  <div className="consultation-detail__highlight-box">
                    {(() => {
                      // lifestyle文字列を解析して日本語表示
                      const parseLifestyle = (lifestyleStr) => {
                        if (!lifestyleStr || typeof lifestyleStr !== 'string') return null;

                        const items = lifestyleStr.split(', ');
                        const lifestyleMap = {
                          'mainFood': '主な食事',
                          'sweetCraving': '甘いもの',
                          'mealTiming': '食事タイミング',
                          'waterIntake': '水分摂取',
                          'sleepDuration': '睡眠時間',
                          'sleepQuality': '睡眠の質',
                          'exerciseFrequency': '運動頻度',
                          'dailyActivity': '日常活動',
                          'stressLevel': 'ストレスレベル',
                          'relaxationMethod': 'リラックス方法'
                        };

                        const getValueByContext = (key, value) => {
                          const contextMaps = {
                            'mainFood': {
                              'rice_heavy': '米・パン中心（炭水化物多め）',
                              'protein_heavy': 'タンパク質・野菜中心',
                              'carb_heavy': '炭水化物中心',
                              'balanced': 'バランス良く摂取',
                              'irregular': '不規則・偏りがち'
                            },
                            'sweetCraving': {
                              'none': 'ほとんど食べない',
                              'sometimes': 'たまに食べる',
                              'daily': '毎日食べる',
                              'frequent': '頻繁に欲しくなる'
                            },
                            'mealTiming': {
                              'regular': '規則正しく3食',
                              'skip_breakfast': '朝食を抜くことが多い',
                              'late_dinner': '夕食が遅い',
                              'irregular': '食事時間が不規則'
                            },
                            'waterIntake': {
                              'low': '1L未満',
                              'moderate': '1〜1.5L',
                              'adequate': '1.5〜2L',
                              'high': '2L以上'
                            },
                            'sleepDuration': {
                              'short': '5時間未満',
                              'insufficient': '5〜6時間',
                              'adequate': '7〜8時間',
                              'long': '9時間以上'
                            },
                            'sleepQuality': {
                              'poor': '眠りが浅い・途中で起きる',
                              'fair': 'まあまあ眠れる',
                              'good': 'ぐっすり眠れる',
                              'varies': '日によって違う'
                            },
                            'exerciseFrequency': {
                              'none': 'ほとんど運動しない',
                              'light': '軽い運動（週1〜2回）',
                              'moderate': '適度な運動（週3〜4回）',
                              'heavy': '頻繁に運動（週5回以上）'
                            },
                            'dailyActivity': {
                              'sedentary': '座り仕事中心・移動が少ない',
                              'light': '軽い活動（立ち仕事・家事など）',
                              'moderate': '中程度の活動（歩く機会が多い）',
                              'active': '活発（体を動かす仕事・趣味）'
                            },
                            'stressLevel': {
                              'low': 'あまりストレスを感じない',
                              'moderate': '適度なストレス',
                              'high': '強いストレスを感じる',
                              'overwhelming': '非常に強いストレス'
                            },
                            'relaxationMethod': {
                              'none': '特にない',
                              'hobby': '趣味・読書・音楽など',
                              'exercise': '運動・散歩',
                              'meditation': '瞑想・ヨガ・入浴',
                              'social': '人との会話・交流'
                            }
                          };

                          return contextMaps[key]?.[value] || value;
                        };

                        return items.map(item => {
                          const [key, value] = item.split(': ');
                          const japaneseKey = lifestyleMap[key] || key;
                          const japaneseValue = getValueByContext(key, value);
                          return { key: japaneseKey, value: japaneseValue };
                        }).filter(item => item.key && item.value);
                      };

                      const lifestyleItems = parseLifestyle(consultation.userInput.lifestyle);

                      if (!lifestyleItems || lifestyleItems.length === 0) {
                        return (
                          <p className="consultation-detail__text-content">
                            {consultation.userInput.lifestyle}
                          </p>
                        );
                      }

                      return (
                        <div className="consultation-detail__lifestyle-grid">
                          {lifestyleItems.map((item, index) => (
                            <div key={index} className="consultation-detail__lifestyle-item">
                              <span className="consultation-detail__lifestyle-key">{item.key}:</span>
                              <span className="consultation-detail__lifestyle-value">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Diet */}
              {consultation.userInput.diet && (
                <div>
                  <span className="consultation-detail__section-title">食生活</span>
                  <p className="consultation-detail__text-content">
                    {consultation.userInput.diet}
                  </p>
                </div>
              )}

              {/* Consultation Question */}
              {consultation.userInput.consultation && (
                <div>
                  <span className="consultation-detail__section-title">具体的な相談内容</span>
                  <div className="consultation-detail__highlight-box">
                    <p className="consultation-detail__text-content">
                      {consultation.userInput.consultation}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI Advice Section - 構造化表示 */}
        <div className="consultation-detail__section">
          <StructuredAdviceDisplay
            aiAdvice={consultation.aiAdvice}
            consultationContent={consultation.userInput?.consultation}
            basicInfo={consultation.userInput}
            labValues={consultation.userInput?.labValues}
            doctorConsult={consultation.doctorConsult}
            consultationId={consultation._id}
            onNewConsultation={() => navigate('/form-v2')}
            onContinueConsultation={handleContinueConsultation}
          />
        </div>

        {/* アフィリエイト商品カルーセル: アドバイス読了後のおすすめ商品 */}
        <ProductCarousel location="form_page" />

        {/* Back to Dashboard */}
        <div className="consultation-detail__footer">
          <Link to="/how-to-use" className="consultation-detail__guide-link">
            📖 使い方ガイドを見る
          </Link>
          <button
            onClick={() => navigate('/mypage')}
            className="tsumugi-btn tsumugi-btn-outline"
          >
            📱 マイページへ戻る
          </button>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="tsumugi-btn tsumugi-btn-primary consultation-detail__scroll-top"
          aria-label="ページトップへ戻る"
        >
          <svg
            className="consultation-detail__scroll-top-icon"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
    </AppLayout>
  );
};

export default ConsultationDetailPage;
